import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import { AuthProvider } from "@/contexts/AuthContext";
import { OfflineSyncProvider } from "@/contexts/OfflineSyncContext";
import { Toaster } from "@/components/ui/sonner";

// Code splitting: cada página se carga bajo demanda (chunk propio).
// Esto reduce drásticamente el bundle inicial y acelera la primera carga.
const Ejecutivo = lazy(() => import("@/pages/Ejecutivo"));
const BBS = lazy(() => import("@/pages/BBS"));
const EhsSafety = lazy(() => import("@/pages/EhsSafety"));
const EhsFlow = lazy(() => import("@/pages/EhsFlow"));
const LegalFlow = lazy(() => import("@/pages/LegalFlow"));
const A3Flow = lazy(() => import("@/pages/A3Flow"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projects = lazy(() => import("@/pages/Projects"));
const NewAnalysis = lazy(() => import("@/pages/NewAnalysis"));
const AnalysisView = lazy(() => import("@/pages/AnalysisView"));
const History = lazy(() => import("@/pages/History"));
const Normas = lazy(() => import("@/pages/Normas"));
const Actions = lazy(() => import("@/pages/Actions"));
const A3List = lazy(() => import("@/pages/A3List"));
const RegistroSuscripcion = lazy(() => import("@/pages/RegistroSuscripcion"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const FirmarRecorrido = lazy(() => import("@/pages/seguridad/FirmarRecorrido"));
const ValidarDC3 = lazy(() => import("@/pages/ValidarDC3"));
const TvLiga = lazy(() => import("@/pages/TvLiga"));
const SystemAccess = lazy(() => import("@/pages/SystemAccess"));
const ChangePasswordRequired = lazy(() => import("@/pages/ChangePasswordRequired"));
const EppDashboard = lazy(() => import("@/pages/epp/EppDashboard"));
const Inventory = lazy(() => import("@/pages/epp/Inventory"));
const Movements = lazy(() => import("@/pages/epp/Movements"));
const Requests = lazy(() => import("@/pages/epp/Requests"));
const Assignments = lazy(() => import("@/pages/epp/Assignments"));
const Matrix = lazy(() => import("@/pages/epp/Matrix"));
const Users = lazy(() => import("@/pages/epp/Users"));
const Permissions = lazy(() => import("@/pages/admin/Permissions"));
const PermissionsAudit = lazy(() => import("@/pages/admin/PermissionsAudit"));
const Bitacora = lazy(() => import("@/pages/admin/Bitacora"));
const ControlAccesos = lazy(() => import("@/pages/admin/ControlAccesos"));
const Empresas = lazy(() => import("@/pages/admin/Empresas"));
const JaredConfig = lazy(() => import("@/pages/admin/JaredConfig"));
const DiagnosticoPermisos = lazy(() => import("@/pages/admin/DiagnosticoPermisos"));
const PersonalizarEmpresa = lazy(() => import("@/pages/PersonalizarEmpresa"));
const Areas = lazy(() => import("@/pages/admin/Areas"));
const Capacitaciones = lazy(() => import("@/pages/salud/Capacitaciones"));
const PlanAnualCapacitacion = lazy(() => import("@/pages/salud/PlanAnualCapacitacion"));
const AsistenciasCapacitacion = lazy(() => import("@/pages/salud/AsistenciasCapacitacion"));
const MiCapacitacion = lazy(() => import("@/pages/salud/MiCapacitacion"));
const CumplimientoCapacitacion = lazy(() => import("@/pages/salud/CumplimientoCapacitacion"));
const DashboardCapacitacion = lazy(() => import("@/pages/salud/DashboardCapacitacion"));
const DNC = lazy(() => import("@/pages/salud/DNC"));
const ELearning = lazy(() => import("@/pages/salud/ELearning"));
const Directorio = lazy(() => import("@/pages/proveedores/Directorio"));
const Documentacion = lazy(() => import("@/pages/proveedores/Documentacion"));
const Personal = lazy(() => import("@/pages/proveedores/Personal"));
const Permisos = lazy(() => import("@/pages/proveedores/Permisos"));
const AST = lazy(() => import("@/pages/seguridad/AST"));
const InstruccionVisual = lazy(() => import("@/pages/seguridad/InstruccionVisual"));
const VerificarPermiso = lazy(() => import("@/pages/proveedores/VerificarPermiso"));
const Vehiculos = lazy(() => import("@/pages/proveedores/Vehiculos"));
const Checkin = lazy(() => import("@/pages/proveedores/Checkin"));
const Auditoria = lazy(() => import("@/pages/Auditoria"));
const AuditoriaDetalle = lazy(() => import("@/pages/AuditoriaDetalle"));
const LeccionesAprendidas = lazy(() => import("@/pages/LeccionesAprendidas"));
const SaludDashboard = lazy(() => import("@/pages/salud/SaludDashboard"));
const Incapacidades = lazy(() => import("@/pages/salud/Incapacidades"));
const VigilanciaMedica = lazy(() => import("@/pages/salud/VigilanciaMedica"));
const Expediente = lazy(() => import("@/pages/salud/Expediente"));
const VigilanciaExposicion = lazy(() => import("@/pages/salud/VigilanciaExposicion"));
const DIR = lazy(() => import("@/pages/DIR"));
const Ergonomia = lazy(() => import("@/pages/salud/Ergonomia"));
const Psicosocial = lazy(() => import("@/pages/salud/Psicosocial"));
const PsicoPublico = lazy(() => import("@/pages/salud/PsicoPublico"));
const Medicamentos = lazy(() => import("@/pages/salud/Medicamentos"));
const ConsultasMedicas = lazy(() => import("@/pages/salud/ConsultasMedicas"));
const GenerarDC3 = lazy(() => import("@/pages/salud/GenerarDC3"));
const AmbienteDashboard = lazy(() => import("@/pages/ambiente/AmbienteDashboard"));
const AspectosImpactos = lazy(() => import("@/pages/ambiente/AspectosImpactos"));
const ResumenAmbiental = lazy(() => import("@/pages/ambiente/ResumenAmbiental"));
const Residuos = lazy(() => import("@/pages/ambiente/Residuos"));
const BitacoraResiduos = lazy(() => import("@/pages/ambiente/BitacoraResiduos"));
const PasesSalida = lazy(() => import("@/pages/ambiente/PasesSalida"));
const Consumos = lazy(() => import("@/pages/ambiente/Consumos"));
const Cumplimiento = lazy(() => import("@/pages/ambiente/Cumplimiento"));
const NomInspections = lazy(() => import("@/pages/seguridad/NomInspections"));
const NomInspectionNew = lazy(() => import("@/pages/seguridad/NomInspectionNew"));
const NomInspectionDetail = lazy(() => import("@/pages/seguridad/NomInspectionDetail"));
const NomCapas = lazy(() => import("@/pages/seguridad/NomCapas"));
const ActosCondiciones = lazy(() => import("@/pages/seguridad/ActosCondiciones"));
const A3New = lazy(() => import("@/pages/A3New"));
const A3Detail = lazy(() => import("@/pages/A3Detail"));
const NormaCumplimiento = lazy(() => import("@/pages/seguridad/NormaCumplimiento"));
const Procedimientos = lazy(() => import("@/pages/seguridad/Procedimientos"));
const ProcedimientoDetalle = lazy(() => import("@/pages/seguridad/ProcedimientoDetalle"));
const InspeccionesPlanta = lazy(() => import("@/pages/seguridad/InspeccionesPlanta"));
const Inventario = lazy(() => import("@/pages/inventario/Inventario"));
const InspeccionPlantaNueva = lazy(() => import("@/pages/seguridad/InspeccionPlantaNueva"));
const InspeccionPlantaDetalle = lazy(() => import("@/pages/seguridad/InspeccionPlantaDetalle"));
const Ranking = lazy(() => import("@/pages/Ranking"));
const PoliticaSeguridad = lazy(() => import("@/pages/PoliticaSeguridad"));
const ProgramaSeguimiento = lazy(() => import("@/pages/ProgramaSeguimiento"));
const Suscripcion = lazy(() => import("@/pages/Suscripcion"));

function RedirectAnalysis() {
  const { id } = useParams();
  return <Navigate to={`/seguridad/analysis/${id}`} replace />;
}

// Fallback ligero mientras se descarga el chunk de la ruta.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
      <div className="h-8 w-8 rounded-full border-4 border-slate-200 border-t-[var(--brand,#E31E24)] animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OfflineSyncProvider>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/registro-suscripcion" element={<RegistroSuscripcion />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/firmar-recorrido/:plantId/:rid/:token" element={<FirmarRecorrido />} />
            <Route path="/validar-dc3/:folio" element={<ValidarDC3 />} />
            <Route path="/tv/liga/:plantId/:token" element={<TvLiga />} />
            <Route path="/p/psico/:plantId/:token" element={<PsicoPublico />} />
            <Route path="/system-access" element={<SystemAccess />} />
            <Route path="/change-password" element={<ChangePasswordRequired />} />
            {/* Página de Suscripción — fuera del Layout para bloquear tenants sin pago */}
            <Route path="/suscripcion" element={<ProtectedRoute><Suscripcion /></ProtectedRoute>} />
            {/* QR verification target — requires login, clean full-page view */}
            <Route path="/verificar-permiso/:id" element={<ProtectedRoute><VerificarPermiso /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {/* Main unified dashboard */}
              <Route index element={<Ejecutivo />} />
              <Route path="bbs" element={<BBS />} />
              <Route path="ehs" element={<EhsSafety />} />
              <Route path="ejecutivo" element={<Navigate to="/" replace />} />

              {/* SEGURIDAD */}
              <Route path="seguridad" element={<Dashboard />} />
              <Route path="seguridad/analysis/new" element={<NewAnalysis />} />
              <Route path="seguridad/analysis/:id" element={<AnalysisView />} />
              <Route path="seguridad/nom-inspections" element={<NomInspections />} />
              <Route path="seguridad/nom-inspections/new" element={<NomInspectionNew />} />
              <Route path="seguridad/nom-inspections/capas" element={<NomCapas />} />
              <Route path="seguridad/nom-inspections/:id" element={<NomInspectionDetail />} />
              <Route path="seguridad/history" element={<History />} />
              <Route path="seguridad/a3" element={<A3List />} />
              <Route path="seguridad/a3/new" element={<A3New />} />
              <Route path="seguridad/a3/:id/edit" element={<A3New />} />
              <Route path="seguridad/a3/:id" element={<A3Detail />} />
              <Route path="seguridad/actions" element={<Actions />} />
              <Route path="seguridad/actos-condiciones" element={<ActosCondiciones />} />
              <Route path="seguridad/normas" element={<Normas />} />
              <Route path="seguridad/normas/:nom/cumplimiento" element={<NormaCumplimiento />} />
              <Route path="seguridad/normas/:nom" element={<Normas />} />
              <Route path="seguridad/procedimientos" element={<Procedimientos />} />
              <Route path="seguridad/procedimientos/:nom" element={<ProcedimientoDetalle />} />
              <Route path="seguridad/inspecciones-planta" element={<InspeccionesPlanta />} />
              <Route path="seguridad/inspecciones-planta/nueva" element={<InspeccionPlantaNueva />} />
              <Route path="seguridad/inspecciones-planta/:id" element={<InspeccionPlantaDetalle />} />
              <Route path="seguridad/inventario" element={<Inventario />} />
              <Route path="ranking" element={<Ranking />} />
              <Route path="politica-seguridad" element={<PoliticaSeguridad />} />
              <Route path="programa-seguimiento" element={<ProgramaSeguimiento />} />
              <Route path="ehs-flow" element={<EhsFlow />} />

              {/* SEGURIDAD - EPP submodule */}
              <Route path="seguridad/epp" element={<EppDashboard />} />
              <Route path="seguridad/epp/inventory" element={<Inventory />} />
              <Route path="seguridad/epp/movements" element={<Movements />} />
              <Route path="seguridad/epp/requests" element={<Requests />} />
              <Route path="seguridad/epp/assignments" element={<Assignments />} />
              <Route path="seguridad/epp/matrix" element={<Matrix />} />

              {/* SALUD OCUPACIONAL */}
              <Route path="salud" element={<SaludDashboard />} />
              <Route path="dir" element={<DIR />} />
              <Route path="salud/incapacidades" element={<Incapacidades />} />
              <Route path="salud/capacitaciones" element={<Capacitaciones />} />
              <Route path="salud/capacitaciones/plan-anual" element={<PlanAnualCapacitacion />} />
              <Route path="salud/capacitaciones/asistencias" element={<AsistenciasCapacitacion />} />
              <Route path="salud/capacitaciones/cumplimiento" element={<CumplimientoCapacitacion />} />
              <Route path="salud/capacitaciones/dashboard" element={<DashboardCapacitacion />} />
              <Route path="salud/capacitaciones/dnc" element={<DNC />} />
              <Route path="salud/capacitaciones/elearning" element={<ELearning />} />
              <Route path="mi-capacitacion" element={<MiCapacitacion />} />
              <Route path="proveedores" element={<Directorio />} />
              <Route path="proveedores/documentacion" element={<Documentacion />} />
              <Route path="proveedores/personal" element={<Personal />} />
              <Route path="seguridad/permisos-trabajo" element={<Permisos />} />
              <Route path="seguridad/ast" element={<AST />} />
              <Route path="seguridad/instruccion-visual" element={<InstruccionVisual />} />
              <Route path="proveedores/permisos" element={<Navigate to="/seguridad/permisos-trabajo" replace />} />
              <Route path="proveedores/vehiculos" element={<Vehiculos />} />
              <Route path="proveedores/checkin" element={<Checkin />} />
              <Route path="salud/vigilancia" element={<VigilanciaMedica />} />
              <Route path="salud/expediente" element={<Expediente />} />
              <Route path="salud/vigilancia-exposicion" element={<VigilanciaExposicion />} />
              <Route path="salud/ergonomia" element={<Ergonomia />} />
              <Route path="salud/psicosocial" element={<Psicosocial />} />
              <Route path="salud/medicamentos" element={<Medicamentos />} />
              <Route path="salud/consultas" element={<ConsultasMedicas />} />
              <Route path="salud/capacitaciones/generar-dc3" element={<GenerarDC3 />} />

              {/* MEDIO AMBIENTE */}
              <Route path="ambiente" element={<AmbienteDashboard />} />
              <Route path="ambiente/aspectos-impactos" element={<AspectosImpactos view="aspectos" />} />
              <Route path="ambiente/aspectos-impactos/inventario-residuos" element={<AspectosImpactos view="residuos" />} />
              <Route path="ambiente/resumen" element={<ResumenAmbiental />} />
              <Route path="ambiente/residuos" element={<Residuos />} />
              <Route path="ambiente/bitacora-residuos" element={<BitacoraResiduos />} />
              <Route path="ambiente/residuos/pases-salida" element={<PasesSalida />} />
              <Route path="ambiente/consumos" element={<Consumos defaultTipo="agua" />} />
              <Route path="ambiente/energia" element={<Consumos defaultTipo="electricidad" />} />
              <Route path="ambiente/cumplimiento" element={<Cumplimiento />} />
              <Route path="flujo-legal" element={<LegalFlow />} />
              <Route path="flujo-a3" element={<A3Flow />} />

              {/* Config */}
              <Route path="config/projects" element={<Projects />} />
              <Route path="auditoria" element={<Auditoria />} />
              <Route path="auditoria/:id" element={<AuditoriaDetalle />} />
              <Route path="lecciones" element={<LeccionesAprendidas />} />
              <Route path="admin/users" element={
                <ProtectedRoute roles={["gerente"]}><Users /></ProtectedRoute>
              } />
              <Route path="admin/permissions" element={
                <ProtectedRoute requireSuper><Permissions /></ProtectedRoute>
              } />
              <Route path="admin/permissions/audit" element={
                <ProtectedRoute requireSuper><PermissionsAudit /></ProtectedRoute>
              } />
              <Route path="admin/bitacora" element={
                <ProtectedRoute requireSuper><Bitacora /></ProtectedRoute>
              } />
              <Route path="admin/accesos" element={
                <ProtectedRoute requireSuper><ControlAccesos /></ProtectedRoute>
              } />
              <Route path="admin/empresas" element={
                <ProtectedRoute requireSuper><Empresas /></ProtectedRoute>
              } />
              <Route path="admin/areas" element={
                <ProtectedRoute roles={["gerente"]}><Areas /></ProtectedRoute>
              } />
              <Route path="admin/jared" element={
                <ProtectedRoute roles={["gerente"]}><JaredConfig /></ProtectedRoute>
              } />
              <Route path="admin/diagnostico-permisos" element={
                <ProtectedRoute roles={["gerente", "coordinador_ehs"]}><DiagnosticoPermisos /></ProtectedRoute>
              } />
              <Route path="personalizar" element={
                <ProtectedRoute roles={["gerente"]}><PersonalizarEmpresa /></ProtectedRoute>
              } />

              {/* Legacy redirects */}
              <Route path="projects" element={<Navigate to="/config/projects" replace />} />
              <Route path="analysis/new" element={<Navigate to="/seguridad/analysis/new" replace />} />
              <Route path="analysis/:id" element={<RedirectAnalysis />} />
              <Route path="history" element={<Navigate to="/seguridad/history" replace />} />
              <Route path="a3" element={<Navigate to="/seguridad/a3" replace />} />
              <Route path="actions" element={<Navigate to="/seguridad/actions" replace />} />
              <Route path="normas" element={<Navigate to="/seguridad/normas" replace />} />
              <Route path="epp" element={<Navigate to="/seguridad/epp" replace />} />
              <Route path="epp/inventory" element={<Navigate to="/seguridad/epp/inventory" replace />} />
              <Route path="epp/movements" element={<Navigate to="/seguridad/epp/movements" replace />} />
              <Route path="epp/requests" element={<Navigate to="/seguridad/epp/requests" replace />} />
              <Route path="epp/assignments" element={<Navigate to="/seguridad/epp/assignments" replace />} />
              <Route path="epp/matrix" element={<Navigate to="/seguridad/epp/matrix" replace />} />
            </Route>
          </Routes>
          </Suspense>
          </OfflineSyncProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
